import type { HTMLAttributes } from 'react';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?:   string | number;
  height?:  string | number;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  lines?:   number;
}

const roundedClasses = {
  sm:   'rounded-sm',
  md:   'rounded-md',
  lg:   'rounded-lg',
  xl:   'rounded-xl',
  full: 'rounded-full',
};

function SkeletonBase({ width, height, rounded = 'md', className = '', style, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      style={{
        width:  typeof width  === 'number' ? `${width}px`  : width,
        height: typeof height === 'number' ? `${height}px` : height,
        ...style,
      }}
      className={[
        'animate-shimmer',
        roundedClasses[rounded],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    />
  );
}

export function Skeleton({ lines = 1, ...props }: SkeletonProps) {
  if (lines > 1) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonBase
            key={i}
            height={16}
            style={{ width: i === lines - 1 ? '75%' : '100%' }}
            {...props}
          />
        ))}
      </div>
    );
  }
  return <SkeletonBase {...props} />;
}

export function SkeletonCard() {
  return (
    <div className="rounded-xl border border-(--border) bg-(--surface) p-6 space-y-4" aria-hidden="true">
      <Skeleton height={200} rounded="lg" className="w-full" />
      <div className="space-y-2">
        <Skeleton height={20} width="60%" />
        <Skeleton lines={2} />
      </div>
      <div className="flex gap-2">
        <Skeleton height={24} width={60} rounded="full" />
        <Skeleton height={24} width={60} rounded="full" />
        <Skeleton height={24} width={60} rounded="full" />
      </div>
    </div>
  );
}

export function SkeletonAvatar({ size = 40 }: { size?: number }) {
  return <Skeleton width={size} height={size} rounded="full" />;
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return <Skeleton lines={lines} height={16} />;
}

export default Skeleton;
